import { UserPopover } from "@/components/Popover/User";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import ErrorBoundary from "@/ErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Application } from "types";

export default function ViewApplication({ item, me, applications }: { item: Application, me: any, applications: any }) {
    const { teamId, channelId, applicationId } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [disabled, setDisabled] = useState(false);
    const handleApplicationSubmit = async (status: string) => {
        setDisabled(true);
        const response = await fetch(`__SITE_BASE__/api/v1/teams/${teamId}/applications/${applicationId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ status }),
        });

        if (response.ok) {
            applications.refetch();
            navigate(`/teams/${teamId}/channels/${channelId}`);
        } else {
            const data = await response.json();
            setError(data.message);
            setDisabled(false);
        }
    };
    const queryClient = useQueryClient();
    const members = (queryClient.getQueryState(["team", teamId, "members"]) as any);

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-col w-full p-4 gap-4">
                <div className="flex justify-between items-center gap-4">
                    <div className="font-semibold text-white">Application</div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => {
                                navigate(`/teams/${teamId}/channels/${channelId}`);
                            }}
                            className="bg-brand-green hover:bg-brand-green-dark rounded-lg"
                        >
                            Back
                        </Button>
                    </div>
                </div>
                <div className="flex flex-col w-full bg-brand-dark rounded-lg p-4">
                    {error && <div className="text-red-500">{error}</div>}
                    <ErrorBoundary>
                        <div className="sm:flex items-center gap-4 sm:justify-between">
                            <UserPopover user={{
                                ...item.submittedBy,
                                roles: typeof members?.data?.members?.find((member: any) => member.id === item.submittedBy.id)?.roles !== "undefined" ? members.data?.members?.find((member: any) => member.id === item.submittedBy.id)?.roles : ["leave"],
                            }}>
                                <div className="flex items-center gap-2 cursor-pointer">
                                    <Avatar>
                                        <AvatarImage src={item.submittedBy.profilePicture} />
                                        <AvatarFallback className="bg-brand-profile">{item.submittedBy.username[0]}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col">
                                        <div className="font-semibold text-white">{item.submittedBy.username}</div>
                                    </div>
                                </div>
                            </UserPopover>
                            <div className="flex flex-col sm:items-end">
                                <div className="font-semibold text-white">Status</div>
                                <div className="flex gap-2 items-center">
                                    <div
                                        className={`font-semibold ${item.status === "pending"
                                            ? "text-yellow-500"
                                            : item.status === "accepted"
                                                ? "text-green-500"
                                                : "text-red-500"
                                            }`}
                                    >
                                        {item.status === "pending" ? "Pending" : item.status === "accepted" ? "Approved" : "Rejected"}
                                    </div>
                                    <hr className="w-4 bg-gray-600" />
                                    <div className="text-gray-300">{new Date(item.createdAt).toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 mt-4">
                            {item.answers.map((answer, index) => (
                                <div key={index} className="flex flex-col">
                                    <div className="font-semibold text-white">{answer.question}</div>
                                    <div className="flex items-center w-full gap-2">
                                        <div className="w-8 h-8 min-w-[2rem] bg-brand-profile text-brand-green rounded-lg font-semibold text-lg flex items-center justify-center">
                                            {index + 1}
                                        </div>
                                        <div className="break-words min-w-80 sm:min-w-[66rem] sm:max-w-[66rem] items-center">
                                            <div className="text-gray-300 bg-brand-darkest w-full rounded-lg py-2 px-2 break-words">
                                                {answer.answer}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-4 mt-4">
                            {item.status === "pending" && (
                                <>
                                    <Button
                                        onClick={() => {
                                            handleApplicationSubmit("accepted");
                                        }}
                                        disabled={disabled}
                                        className="bg-green-500 hover:bg-green-600 rounded-lg disabled:opacity-50"
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            handleApplicationSubmit("rejected");
                                        }}
                                        disabled={disabled}
                                        className="bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50"
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}
                        </div>
                    </ErrorBoundary>
                </div>
            </div>
        </div>
    );
}
